import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Drawer from "@mui/material/Drawer";
import { useTheme } from "@mui/material/styles";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import SidenavCollapse from "components/navigation/Sidenav/SidenavCollapse";
import SidenavRoot from "components/navigation/Sidenav/SidenavRoot";
import sidenavLogoLabel from "components/navigation/Sidenav/styles/sidenav";
import linearGradient from "assets/theme/functions/linearGradient";
import { useUIController, setMiniSidenav } from "context";
import BrandLogo from "assets/images/logos/porteriq-logo.svg";
import BrandLogoMark from "assets/images/logos/porteriq-logo-icon.png";
import { jwtService } from "services";
import { useDashboardBreakpoints } from "hooks/useDashboardBreakpoints";

const LOGO_BAND_HEIGHT_PX = 88;

function Sidenav({ color, brandName, routes, ...rest }) {
  const theme = useTheme();
  const { isMobileNav, isCompactRail } = useDashboardBreakpoints();
  const isMobile = isMobileNav;

  const [controller, dispatch] = useUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openCollapse, setOpenCollapse] = useState("digital-presence");
  const [activeKey, setActiveKey] = useState("");

  const compactRailPrevRef = useRef();

  useEffect(() => {
    if (isMobile) {
      setMobileOpen(!miniSidenav);
    } else {
      setMobileOpen(false);
    }
  }, [isMobile, miniSidenav]);

  useEffect(() => {
    if (isMobile) {
      compactRailPrevRef.current = isCompactRail;
      return;
    }
    const wasCompact = compactRailPrevRef.current;
    if (isCompactRail) {
      if (wasCompact === undefined || wasCompact === false) {
        setMiniSidenav(dispatch, true);
      }
    }
    compactRailPrevRef.current = isCompactRail;
  }, [isMobile, isCompactRail, dispatch]);

  useEffect(() => {
    let currentKey = "";
    let parentKey = "";

    function findActiveKeys(routeList, currentPath) {
      for (const route of routeList) {
        if (currentPath === route.route) {
          currentKey = route.key;
        }
        if (route.collapse) {
          for (const subRoute of route.collapse) {
            if (currentPath === subRoute.route) {
              currentKey = subRoute.key;
              parentKey = route.key;
              return;
            }
          }
        }
      }
    }

    findActiveKeys(routes, pathname);
    setActiveKey(currentKey);
    setOpenCollapse(parentKey || "digital-presence");
  }, [pathname, routes]);

  const closeSidenav = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = useCallback(() => {
    if (isMobile) {
      setMobileOpen((o) => !o);
    } else {
      setMiniSidenav(dispatch, !miniSidenav);
    }
  }, [isMobile, dispatch, miniSidenav]);

  const drawerBackground = linearGradient(
    theme.palette.gradients.sidenav.main,
    theme.palette.gradients.sidenav.state,
    theme.palette.gradients.sidenav.deg
  );

  useEffect(() => {
    window.toggleSidenav = handleDrawerToggle;
    window.isSidenavOpen = isMobile ? mobileOpen : !miniSidenav;
    return () => {
      delete window.toggleSidenav;
      delete window.isSidenavOpen;
    };
  }, [isMobile, mobileOpen, miniSidenav, handleDrawerToggle]);

  const renderRoutes = (routesToRender) => {
    const isAdmin = jwtService.hasRole("ADMIN");
    const userInfo = jwtService.getUserInfo();
    // enabledPages from JWT — null means no restriction (backward compat / admin users)
    const enabledPages = userInfo?.enabledPages || null;
    // lockedPages from JWT — visible in nav but inaccessible (upsell prompt)
    const lockedPages = userInfo?.lockedPages || [];

    return routesToRender
      .filter(route => {
        if (route.hidden) return false;
        if (route.adminOnly && !route.requiredRole && !isAdmin) return false;
        if (route.key === "sign-in" && jwtService.isAuthenticated()) return false;
        // enabledPages guard: only show routes in enabledPages OR lockedPages.
        // Locked pages are visible (just inaccessible) so they must pass the filter.
        // Routes without a meaningful key (dividers, titles, auth/utility) are always shown.
        // Admins bypass this filter entirely — they see everything.
        if (!isAdmin && enabledPages && route.type === "collapse" && route.key) {
          const nonPageKeys = ["admin-panel", "profile", "logout", "sign-in"];
          if (!nonPageKeys.includes(route.key)
              && !enabledPages.includes(route.key)
              && !lockedPages.includes(route.key)) {
            return false;
          }
        }
        return true;
      })
      .map((routeItem) => {
        const { type, name, icon, title, noCollapse, key, href, route, collapse } = routeItem;
        // Lock state: JWT lockedPages takes precedence for authenticated users.
        // routeItem.locked is kept as a static fallback for routes with no `route` property
        // (e.g. kol-insights) — ensures they never hit the NavLink fallthrough unauthenticated.
        const isLocked = routeItem.locked || (!isAdmin && lockedPages.includes(key)) || (routeItem.adminOnly && !isAdmin);

        const handleCollapseClick = (collapseKey) => {
          setOpenCollapse(openCollapse === collapseKey ? "" : collapseKey);
          setActiveKey(collapseKey);
        };

        if (type === "collapse") {
          if (collapse) {
            return (
              <SidenavCollapse
                key={key}
                name={name}
                icon={icon}
                active={false}
                open={openCollapse === key}
                onClick={() => handleCollapseClick(key)}
                locked={isLocked}
              >
                <List sx={{ py: 0.5, pl: 2 }}>
                  {renderRoutes(collapse)}
                </List>
              </SidenavCollapse>
            );
          }
          if (href) {
            return (
              <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
                <SidenavCollapse name={name} icon={icon} active={activeKey === key} noCollapse={noCollapse} />
              </Link>
            );
          }
          if (isLocked) {
            return (
              <div key={key} style={{ cursor: "not-allowed" }}>
                <SidenavCollapse name={name} icon={icon} active={activeKey === key} noCollapse={noCollapse} locked={isLocked} />
              </div>
            );
          }
          return (
            <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
              <SidenavCollapse name={name} icon={icon} active={activeKey === key} noCollapse={noCollapse} />
            </NavLink>
          );
        } else if (type === "title") {
          return (
            <VuiTypography key={key} display="block" variant="caption" fontWeight="bold" textTransform="uppercase" pl={3} mt={2} mb={1} ml={1}>
              {title}
            </VuiTypography>
          );
        } else if (type === "divider") {
          return <Divider key={key} />;
        }
        return null;
      });
  };

  const drawerContent = (
    <>
      <VuiBox
        pt={2}
        pb={0.5}
        px={miniSidenav ? 0 : { xs: 2, sm: 3, md: 4 }}
        textAlign="center"
        sx={{
          overflow: "visible",
          position: "relative",
          boxSizing: "border-box",
          minHeight: LOGO_BAND_HEIGHT_PX,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "center",
        }}
      >
        {isMobile && mobileOpen && (
          <VuiBox
            position="absolute"
            top={0}
            right={0}
            p={1}
            onClick={closeSidenav}
            sx={{ cursor: "pointer", zIndex: 1000 }}
          >
            <VuiTypography variant="h6" color="text">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </VuiTypography>
          </VuiBox>
        )}

        <VuiBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            flex: 1,
            width: "100%",
            minHeight: LOGO_BAND_HEIGHT_PX - 24,
            maxWidth: "100%",
            mx: "auto",
            px: miniSidenav ? 0 : 0.5,
            boxSizing: "border-box",
            pl: miniSidenav && !isMobile ? "10px" : undefined,
          }}
        >
          {miniSidenav && !isMobile ? (
            <VuiBox
              component="img"
              src={BrandLogoMark}
              alt="PorterIQ"
              sx={{
                width: 44,
                height: 44,
                display: "block",
                flexShrink: 0,
                borderRadius: "12px",
                objectFit: "contain",
                boxSizing: "border-box",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                transition: "opacity 0.2s ease-in-out",
                ml: "auto",
                mr: "auto",
              }}
            />
          ) : (
            <VuiBox
              sx={((t) => sidenavLogoLabel(t, { miniSidenav }), {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                width: "100%",
                maxWidth: "100%",
                minHeight: LOGO_BAND_HEIGHT_PX - 24,
              })}
            >
              <VuiBox
                display="flex"
                sx={((t) => sidenavLogoLabel(t, { miniSidenav, transparentSidenav: false }), {
                  mr: miniSidenav ? 0 : 1,
                  justifyContent: "center",
                  width: "100%",
                  maxWidth: "100%",
                  alignItems: "center",
                })}
              >
                <VuiBox
                  component="img"
                  src={BrandLogo}
                  alt="PorterIQ"
                  sx={{
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                    maxWidth: { xs: 120, sm: 150, md: 170, lg: 190 },
                    maxHeight: { xs: 48, sm: 56, md: 72, lg: 80 },
                    marginRight: { xs: 1, sm: "12px" },
                    transition: "opacity 0.2s ease-in-out",
                  }}
                />
              </VuiBox>
              <VuiTypography
                variant="button"
                textGradient
                color="logo"
                fontSize={14}
                letterSpacing={2}
                fontWeight="medium"
                sx={((t) => sidenavLogoLabel(t, { miniSidenav, transparentSidenav: false }), {
                  opacity: miniSidenav ? 0 : 1,
                  maxWidth: miniSidenav ? 0 : "100%",
                  margin: "0 auto",
                  overflow: "hidden",
                })}
              >
                {brandName}
              </VuiTypography>
            </VuiBox>
          )}
        </VuiBox>
      </VuiBox>
      <Divider light />
      <List sx={{ overflow: "visible", py: 0.5, px: 0.25 }}>{renderRoutes(routes)}</List>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeSidenav}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: "280px",
            background: drawerBackground,
            boxShadow: theme.shadows[24],
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      {drawerContent}
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
